import { useRef } from 'react';
import { Form, Formik, FormikHelpers } from 'formik';

import SectionCard from '@/components/common/SectionCard/SectionCard';
import FileInput from '@/components/common/FileInput/FileInput';
import Button from '@/components/common/Button/Button';
import { ALLOWED_FILE_EXTENSIONS, Fields, validationSchema } from './validation/formValidationSchema';
// eslint-disable-next-line import/no-named-as-default
import ValidationAlert from '@/components/common/ValidationAlert/ValidationAlert';
import { FormattingUtils } from '@/utils/Formatting.utils';
import useUploadFile from './hooks/useUploadFile';
import { useGetUploadedFiles } from '../FileUploadHistory/hooks/useGetUploadedFiles';
import { useAddQueryToUrl } from '@/components/common/TagTabs/useAddQueryToUrl';
import { BULK_CL_UPDATE_FILES_PER_PAGE } from '@/config/common.config';
import SmallText from '@/components/common/SmallText/SmallText';
import styles from './bulk-cl-update-form.module.scss';
import { useDownloadTemplateFile } from '../FileUploadHistory/hooks/useDownloadTemplateFile';

export default function BulkClUpdateFormContainer() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { onFileUpload } = useUploadFile();
  const { getUploadedFiles } = useGetUploadedFiles();
  const { getValue } = useAddQueryToUrl();
  const currentPage = parseInt(getValue('page') ?? '1', 10);
  const { downloadTemplateFile } = useDownloadTemplateFile();

  const fileExtTip = `Only files of ${ALLOWED_FILE_EXTENSIONS.join(', ')}
    file ${FormattingUtils.pluralize(ALLOWED_FILE_EXTENSIONS.length, 'format')} can be uploaded`;

  const onSubmit = async (values: Fields, formikHelpers: FormikHelpers<Fields>) => {
    const successCallback = () => {
      formikHelpers.resetForm();
      formikHelpers.validateForm();
      getUploadedFiles(currentPage, BULK_CL_UPDATE_FILES_PER_PAGE);

      if (fileInputRef.current?.value) {
        fileInputRef.current.value = '';
      }
    };

    onFileUpload({ values, successCallback });
  };

  return (
    <SectionCard
      padding={true}
      title={{ titleText: 'Bulk Contract Line Update' }}
    >
      <Formik<Fields>
        initialValues={{ file: undefined }}
        validationSchema={validationSchema}
        validateOnMount={true}
        onSubmit={onSubmit}
      >
        {({ isValid, setFieldValue, setFieldTouched, errors, touched }) => (
          <Form>
            <div className={styles.form}>
              <label
                className={styles.label}
                htmlFor="file"
              >
                Upload File
              </label>

              <div className={styles['input-wrapper']}>
                <FileInput
                  ref={fileInputRef}
                  id="file"
                  name="file"
                  format="text/csv"
                  onChange={(e) => {
                    setFieldValue('file', e.target?.files?.[0]);
                    setFieldTouched('file', true, false);
                  }}
                />

                {errors.file && touched.file && (
                  <ValidationAlert>
                    {errors.file}
                  </ValidationAlert>
                )}

                <SmallText className={styles.small}>{fileExtTip}</SmallText>
              </div>

              <div>
                <Button
                  type="submit"
                  disabled={!isValid}
                >
                  Upload
                </Button>

              </div>

              <div className={styles['sample-link']}>
                <span>
                  Click{' '}
                  <a
                    style={{ fontSize: '16px' }}
                    onClick={downloadTemplateFile}
                  >
                    here
                  </a>{' '}
                  for a sample template/format
                </span>
              </div>
            </div>
          </Form>
        )}
      </Formik>
    </SectionCard>
  );
}
